<template>
  <AdminAppLayout title="HRDOS | Add Salary Schedule">
    <div class="card z-depth-1" style="padding: 40px; border-radius: 30px; position: relative; overflow: hidden;">
      <h5 class="center-align" style="margin-bottom: 10px">Add Salary Schedule</h5>

      <h5 class="center-align blue-text" style="margin-bottom: 20px">
        {{ step }} — {{ stepTitles[step] }}
      </h5>

      <!-- Progress Bar -->
      <div class="progress" style="height: 4px; border-radius: 2px;">
        <div class="determinate" :style="{ width: (step / 2) * 100 + '%' }"></div>
      </div>

      <!-- Full-card Loader -->
      <div v-show="loading" id="loader">
        <div class="preloader-wrapper big active">
          <div class="spinner-layer spinner-blue-only">
            <div class="circle-clipper left"><div class="circle"></div></div>
            <div class="gap-patch"><div class="circle"></div></div>
            <div class="circle-clipper right"><div class="circle"></div></div>
          </div>
        </div>
        <h5 style="margin-top: 15px; text-shadow: 1px 1px 2px white;">{{ loadingText }}</h5>
      </div>

      <!-- Main Content Area -->
      <form @submit.prevent="submitForm" :style="{ opacity: loading ? 0.2 : 1 }">
        
        <!-- ================= STEP 1: UPLOAD ================= -->
        <div v-show="step === 1" class="step">
          <div class="row">
            <div class="col s12 center-align" style="margin-bottom: 15px; margin-top: 20px;">
              <p class="black-text" style="font-size: 1.1rem">Upload the <b>NATIONAL BUDGET CIRCULAR PDF file</b></p>
            </div>

            <div class="file-field input-field col s12 m6 offset-m3">
              <div class="btn blue lighten-1">
                <span>
                  <i class="material-icons left" style="scale: 1.1">picture_as_pdf</i>
                  <b style="font-size: 1.2rem;">File</b>
                </span>
                <input type="file" accept="application/pdf" @change="handleFileUpload">
              </div>
              <div class="file-path-wrapper">
                <input class="file-path" type="text" placeholder="Upload the PDF file" :value="pdfFile ? pdfFile.name : ''" readonly>
              </div>
            </div>

            <!-- Selected Page Preview -->
            <div class="col s12" v-show="selectedPageNum">
              <div id="confirmed-container" style="display: flex;">
                <h6 class="green-text" style="font-weight: bold; margin-bottom: 15px;">
                  Page {{ selectedPageNum }} Selected
                </h6>
                <div ref="singlePagePreviewContainer"></div>
                <button type="button" class="btn-flat mt-2 btn orange lighten-1" @click="showOverlay = true" style="margin-top: 15px; border-radius: 20px;">
                  <i class="material-icons left" style="scale: 1.5;">refresh</i> <b>Change selected page</b>
                </button>
                <span v-if="extractionError" class="helper-text" style="color: red; font-size: 15px; margin-top: 15px;">
                  No tables detected on page, please select another page.
                </span>
              </div>
            </div>
          </div>
        </div>

        <!-- ================= STEP 2: DETAILS & PREVIEW ================= -->
        <div v-show="step === 2" class="step">
          <div class="row" style="margin-top: 20px;">
            <h6 class="green-text text-darken-1 center-align" style="margin-bottom: 30px;">Please review before saving...</h6>

            <div class="input-field col s12 m8 offset-m2">
              <input id="schedule_name" type="text" v-model="scheduleName" required>
              <label for="schedule_name" class="active">Schedule Name</label>
            </div>

            <div class="input-field col s12 m4 offset-m4 center-align">
              <input id="effective_date" type="date" v-model="effectiveDate" :disabled="!isDateEditable" required>
              <label for="effective_date" class="active">Effective Date</label>
              
              <button v-if="!isDateEditable" type="button" class="btn-flat btn yellow darken-1" @click="isDateEditable = true" style="margin-top: 15px; border-radius: 20px;">
                <i class="material-icons left" style="scale: 1.2;">refresh</i><b>Change date</b>
              </button>
              
              <button v-else type="button" class="btn-flat btn green lighten-1" @click="confirmDate" style="margin-top: 15px; border-radius: 20px;">
                <i class="material-icons left" style="scale: 1.2;">check</i><b>Confirm</b>
              </button>
            </div>
          </div>

          <!-- Extracted Data Table -->
          <div style="margin-top: 20px; overflow-x: auto;">
            <table class="extracted-table striped centered" v-if="tableData.length">
              <thead>
                <tr>
                  <th>Salary Grade</th>
                  <th v-for="i in 8" :key="i">Step {{ i }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(row, index) in tableData" :key="index">
                  <td style="font-weight: bold;">{{ row.salary_grade }}</td>
                  <td v-for="i in 8" :key="i">
                    {{ row['step_' + i] ? row['step_' + i].toLocaleString() : '-' }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- Nav Buttons -->
        <div class="right-align" style="margin-top: 30px">
          <button v-show="step === 2 && !saveSuccess" type="button" class="btn-flat" @click="step = 1">Back</button>
          <button v-show="step === 1" type="button" class="btn blue lighten-1" @click="processNext" :disabled="!selectedPageNum"><b>Next</b></button>
          <button v-show="step === 2 && !saveSuccess" type="submit" class="btn green"><i class="material-icons left">save</i><b>Confirm</b></button>
        </div>

        <!-- Success Message -->
        <h5 v-if="saveSuccess" class="green-text center-align mt-4">
          <i class="material-icons left">check_circle</i> Saved Successfully!
        </h5>
      </form>
    </div>

    <!-- ================= PDF PAGE SELECTION OVERLAY ================= -->
    <div id="page-overlay" v-show="showOverlay">
      <div id="overlay-content" class="z-depth-4">
        <p style="margin-top: 0; font-size: 1.2rem;">Select the page that contains the <b>salary grade table</b>.</p>

        <!-- Container for dynamically appended canvases -->
        <div ref="previewContainer" id="preview-container" style="display: flex; flex-wrap: wrap; gap: 15px; justify-content: center; overflow-y: auto; padding: 10px; margin-bottom: 20px; flex: 1; background: #f5f5f5; border-radius: 8px;">
        </div>

        <div style="display: flex; justify-content: center; gap: 15px;">
          <button type="button" class="btn-flat" @click="cancelOverlay">Cancel</button>
          <button type="button" class="btn blue lighten-1" @click="confirmOverlayPage" :disabled="!tempSelectedPageNum">Confirm</button>
        </div>
      </div>
    </div>

  </AdminAppLayout>
</template>

<script>
import { api } from "@/axios";
import AdminAppLayout from "../../layout/AdminAppLayout.vue";

export default {
  name: "AddSalaryGradeView",
  components: { AdminAppLayout },
  data() {
    return {
      step: 1,
      stepTitles: { 1: "Upload PDF Document", 2: "Review & Save" },
      loading: false,
      loadingText: "Processing PDF file...",
      pdfFile: null,
      
      selectedPageNum: null,
      tempSelectedPageNum: null,
      showOverlay: false,
      extractionError: false,
      
      scheduleName: "",
      effectiveDate: "",
      isDateEditable: false,
      tableData: [],
      saveSuccess: false,
    };
  },
  methods: {
    async handleFileUpload(event) {
      const file = event.target.files[0];
      if (!file || file.type !== 'application/pdf') return;

      this.pdfFile = file;
      this.selectedPageNum = null;
      this.tempSelectedPageNum = null;
      this.$refs.previewContainer.innerHTML = ''; 
      
      this.showOverlay = true;
      await this.$nextTick();

      const fileReader = new FileReader();
      fileReader.onload = async () => {
        try {
          const typedarray = new Uint8Array(fileReader.result);
          
          this.pdfDocument = await window.pdfjsLib.getDocument(typedarray).promise;

          for (let pageNum = 1; pageNum <= this.pdfDocument.numPages; pageNum++) {
            const page = await this.pdfDocument.getPage(pageNum);
            const viewport = page.getViewport({ scale: 0.4 });
            
            const canvas = document.createElement('canvas');
            const context = canvas.getContext('2d');
            canvas.height = viewport.height;
            canvas.width = viewport.width;
            canvas.className = 'page-preview';
            
            canvas.onclick = () => {
              document.querySelectorAll('.page-preview').forEach(c => c.classList.remove('selected'));
              canvas.classList.add('selected');
              this.tempSelectedPageNum = pageNum;
            };

            await page.render({ canvasContext: context, viewport: viewport }).promise;
            this.$refs.previewContainer.appendChild(canvas);
          }
        } catch (error) {
          console.error("Failed to parse PDF:", error);
        }
      };
      
      fileReader.readAsArrayBuffer(file);
    },

    cancelOverlay() {
      this.showOverlay = false;
      if (!this.selectedPageNum) {
        this.pdfFile = null;
        const fileInput = document.querySelector('input[type="file"]');
        if(fileInput) fileInput.value = "";
      }
    },

    confirmOverlayPage() {
      this.selectedPageNum = this.tempSelectedPageNum;
      this.showOverlay = false;

      const selectedCanvas = document.querySelector('.page-preview.selected');
      if (selectedCanvas) {
        this.$refs.singlePagePreviewContainer.innerHTML = '';
        const cloneCanvas = document.createElement('canvas');
        cloneCanvas.width = selectedCanvas.width;
        cloneCanvas.height = selectedCanvas.height;
        cloneCanvas.getContext('2d').drawImage(selectedCanvas, 0, 0);
        cloneCanvas.style.maxWidth = '100%';
        cloneCanvas.style.height = 'auto';
        cloneCanvas.style.borderRadius = '8px';
        cloneCanvas.style.border = '2px solid #ccc';
        this.$refs.singlePagePreviewContainer.appendChild(cloneCanvas);
      }
    },

    async processNext() {
      if (!this.pdfFile || !this.selectedPageNum) return;

      this.loadingText = "Extracting Page & details...";
      this.loading = true;
      this.extractionError = false;

      try {
        const arrayBuffer = await this.pdfFile.arrayBuffer();
        const typedarray = new Uint8Array(arrayBuffer);
        const pdf = await window.pdfjsLib.getDocument(typedarray).promise;
        const page = await pdf.getPage(this.selectedPageNum);

        const viewport = page.getViewport({ scale: 1.5 });
        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d');
        canvas.height = viewport.height;
        canvas.width = viewport.width;

        context.fillStyle = 'white';
        context.fillRect(0, 0, canvas.width, canvas.height);

        await page.render({ canvasContext: context, viewport: viewport }).promise;
        
        // FIX 1: Convert to JPEG to drastically reduce payload size for Render's free tier
        const imageBlob = await new Promise(resolve => canvas.toBlob(resolve, 'image/jpeg', 0.8));
        
        const formData = new FormData();
        formData.append('ocr_image', imageBlob, `page_${this.selectedPageNum}.jpg`);

        // FIX 3: Explicitly set multipart/form-data header
        const response = await api.post('/extract-salary-schedule', formData, {
            headers: { 
                'Content-Type': 'multipart/form-data',
            }
        });

        const result = response.data;

        this.scheduleName = result.scheduleName || '';
        this.effectiveDate = result.effectiveDate || '';

        this.$nextTick(() => { if (typeof window.M !== 'undefined') window.M.updateTextFields(); });

        // NEW LOGIC: Tab-Splitting OCR.space Parser
        if (result.data && result.data.length > 0) {
          let parsedTable = [];
          let currentSG = 1;

          result.data.forEach(line => {
            // Split the line by tab characters (\t), remove commas, and clean empty spaces
            let tokens = line.split('\t')
                             .map(t => t.replace(/,/g, '').trim())
                             .filter(t => t !== '');
            
            // Convert to strict numbers, filtering out text like "ANNEX A" or "Salary"
            let nums = tokens.map(t => parseInt(t, 10)).filter(n => !isNaN(n));

            // A valid salary row will have multiple numbers
            if (nums.length >= 2) {
              let grade;
              let stepValues = [];

              // Check if the first number matches our expected Salary Grade (e.g., 2, 3, 4...)
              if (nums[0] === currentSG) {
                grade = nums[0];
                stepValues = nums.slice(1);
              } 
              // Auto-correct for SG 1 if the OCR missed the "1" but captured the 8 salary steps
              else if (nums[0] >= 1000 && nums.length >= 7) {
                grade = currentSG;
                stepValues = nums;
              } 
              else {
                return; // Skip irrelevant numeric rows (like page numbers)
              }

              // Build the row object safely
              if (grade >= 1 && grade <= 33) {
                parsedTable.push({
                  salary_grade: grade,
                  step_1: stepValues[0] || null,
                  step_2: stepValues[1] || null,
                  step_3: stepValues[2] || null,
                  step_4: stepValues[3] || null,
                  step_5: stepValues[4] || null,
                  step_6: stepValues[5] || null,
                  step_7: stepValues[6] || null,
                  step_8: stepValues[7] || null,
                });
                
                // Increment expected grade for the next loop
                currentSG = grade + 1; 
              }
            }
          });

          if (parsedTable.length > 0) {
            this.tableData = parsedTable;
            this.step = 2;
          } else {
            this.extractionError = true;
          }

        } else {
          this.extractionError = true;
        }

      } catch (error) {
        console.error("Extraction error:", error);
        
        if (error.response) {
            console.log("Server error data:", error.response.data);
        }
        
        this.extractionError = true;
      } finally {
        this.loading = false;
      }
    },

    confirmDate() {
      if (!this.effectiveDate) {
        alert("Please enter a valid date.");
        return;
      }
      this.isDateEditable = false;
    },

    async submitForm() {
      if (!this.tableData.length) return;

      this.loadingText = "Saving to Database...";
      this.loading = true;

      try {
        const payload = {
          schedule_name: this.scheduleName,
          effective_date: this.effectiveDate,
          table_data: this.tableData
        };

        await api.post('/save-salary-schedule', payload);

        this.saveSuccess = true;

      } catch (error) {
        console.error("Save error:", error);
        
        if (error.response) {
            console.error("Server responded with:", error.response.data);
        }
      } finally {
        this.loading = false;
      }
    },
  },
  mounted() {
    const pdfScript = document.createElement('script');
    pdfScript.src = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js';
    document.head.appendChild(pdfScript);

    pdfScript.onload = () => {
      window.pdfjsLib.GlobalWorkerOptions.workerSrc = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';
    };
  }
};
</script>

<style scoped>
#loader {
  background-color: rgba(255, 255, 255, 0.9);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  z-index: 10;
  width: 100%;
  height: 100%;
  position: absolute;
  top: 0;
  left: 0;
}

.step {
  position: relative;
  transition: opacity 0.3s ease;
  min-height: 200px;
}

#confirmed-container {
  flex-direction: column;
  align-items: center;
  margin-top: 20px;
  padding: 20px;
  background: #f9f9f9;
  border-radius: 15px;
}

.extracted-table {
  width: 100%;
  border-collapse: collapse;
  margin-top: 15px;
}

.extracted-table th {
  background-color: #f5f5f5;
  padding: 10px;
  border: 1px solid #ddd;
}

.extracted-table td {
  padding: 10px;
  border: 1px solid #ddd;
  text-align: center;
}

.extracted-table tbody tr:hover {
  background-color: #f0f0f0;
}

/* PDF.js Canvas Preview Styles */
:deep(.page-preview) {
  border: 2px solid #ccc;
  border-radius: 8px;
  cursor: pointer;
  transition: transform 0.2s, border-color 0.2s;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
}

:deep(.page-preview:hover) {
  transform: scale(1.02);
}

:deep(.page-preview.selected) {
  border: 4px solid #42a5f5;
  transform: scale(1.05);
}

#page-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  z-index: 9999;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
}

#overlay-content {
  background: white;
  padding: 30px;
  border-radius: 15px;
  text-align: center;
  width: 85%;
  max-width: 900px;
  display: flex;
  flex-direction: column;
  max-height: 90vh;
}
</style>